import { Component, EventEmitter, input, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MatOption } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AutofocusDirective, ButtonComponent } from '@ird/ng-base';
import { IrdResolvedProperty } from '@ird/shared-base';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectOptions } from '../../models';
import { ColorPickerComponent } from '../color-picker';

export interface EditPopoverConfig {
  value: unknown;
  /** Only what the editor reads, so a table of any row type can hand its resolved property over. */
  property: Pick<IrdResolvedProperty, 'type' | 'translationKey' | 'validators'>;
  position: { top: number; left: number };
  selectOptions?: SelectOptions;
}

@Component({
  selector: 'shared-edit-popover',
  templateUrl: './edit-popover.component.html',
  styleUrls: ['./edit-popover.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ButtonComponent,
    TranslatePipe,
    ColorPickerComponent,
    MatSelectModule,
    MatOption,
    AutofocusDirective,
  ],
  host: {
    '(window:keydown.enter)': 'onSave($event)',
    '(window:keydown.escape)': 'onCancel()',
  },
})
export class EditPopoverComponent implements OnInit {
  public config = input.required<EditPopoverConfig>();
  @Output() save = new EventEmitter<unknown>();
  @Output() closed = new EventEmitter<void>();

  editControl!: FormControl;
  fieldType: 'string' | 'number' | 'boolean' | 'date' | 'json' | 'color' | 'textarea' | 'select' = 'string';

  ngOnInit(): void {
    this.determineFieldType();
    this.initializeControl();
  }

  private determineFieldType(): void {
    const type = this.config().property.type;

    if (type === 'select' || type === 'enum') {
      this.fieldType = 'select';
    } else if (type === 'json') {
      this.fieldType = 'json';
    } else if (type === 'color') {
      this.fieldType = 'color';
    } else if (type === 'number') {
      this.fieldType = 'number';
    } else if (type === 'boolean') {
      this.fieldType = 'boolean';
    } else if (type === 'Date') {
      this.fieldType = 'date';
    } else if (type === 'string') {
      if (typeof this.config().value === 'string' && (this.config().value as string).length > 100) {
        this.fieldType = 'textarea';
      } else {
        this.fieldType = 'string';
      }
    } else {
      this.fieldType = 'string';
    }
  }

  private initializeControl(): void {
    let value = this.config().value;

    // Format value based on type
    if (this.fieldType === 'json') {
      value = typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : value || '';
    } else if (this.fieldType === 'date') {
      value = value ? new Date(value as string | number | Date) : null;
    }

    const validators = [];

    if (this.config().property.validators.some((v) => v.name === 'required')) {
      validators.push(Validators.required);
    }

    if (this.fieldType === 'number') {
      validators.push(Validators.pattern(/^-?\d+\.?\d*$/));
    }

    this.editControl = new FormControl(value, validators);
  }

  protected onSave(event: Event): void {
    event.preventDefault();
    if (this.editControl.invalid) {
      this.editControl.markAsTouched();
      return;
    }

    let value = this.editControl.value;

    // Parse value based on type
    if (this.fieldType === 'json') {
      try {
        value = JSON.parse(value);
      } catch (e) {
        // If parsing fails, keep as string
        console.error('JSON parsing error:', e);
        return;
      }
    } else if (this.fieldType === 'number') {
      value = parseFloat(value);
    } else if (this.fieldType === 'date') {
      value = value ? value.toISOString() : null;
    }

    this.save.emit(value);
  }

  protected onCancel(): void {
    this.closed.emit();
  }

  protected getOptionLabel(option: unknown): string {
    const labels = this.config().selectOptions?.labels;
    if (labels && typeof option === 'string') {
      return labels[option] ?? option;
    }
    return String(option);
  }

  protected getErrorMessage(): string {
    if (this.editControl.hasError('required')) {
      return 'ADMINISTRATION.VALIDATIONS.REQUIRED';
    }
    if (this.editControl.hasError('pattern')) {
      return 'ADMINISTRATION.VALIDATIONS.INVALID_ENTRY';
    }
    return '';
  }
}
